'use client';

import { useTranslation } from 'react-i18next';
import {
  useBlockPart,
  useMediaSource,
  type MediaSeriesDetail,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { EmptyState } from '../_shared/EmptyState';
import { EpisodeCard, type EpisodeListStyle } from './EpisodeCard';
import { sectionPad, sectionInner } from '../_shared/styles';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * /video/series/[slug] — series detail with episode list. Honors
 * {@code episodeListStyle} for the rendering register.
 */
export interface SeriesDetailLayoutProps {
  slug: string;
  episodeListStyle?: EpisodeListStyle;
  showEpisodeNumbers?: boolean;
  episodeHrefTemplate?: string;
}

export function SeriesDetailLayout({
  slug,
  episodeListStyle = 'list',
  showEpisodeNumbers = true,
  episodeHrefTemplate,
}: SeriesDetailLayoutProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const media = useMediaSource();
  const state = useAsyncResource<MediaSeriesDetail>(
    async (signal) => {
      if (!media) return null;
      return await media.series.fetchBySlug(slug, signal);
    },
    [media, slug],
  );

  if (!media) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder blockType="SeriesDetailLayout" adapterName="MediaSource" />
        </div>
      </BlockRoot>
    );
  }
  if (state.kind === 'loading') return null;
  if (state.kind !== 'loaded') {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <EmptyState
            {...part('empty')}
            title={
              state.kind === 'error'
                ? t('seriesDetail.error.title')
                : t('seriesDetail.notFound.title')
            }
            hint={
              state.kind === 'error'
                ? t('shared.error.tryAgainLater')
                : t('shared.error.removedOrUnpublished')
            }
          />
        </div>
      </BlockRoot>
    );
  }
  const s = state.data;
  const eps = s.episodes ?? [];

  return (
    <BlockRoot as="article" style={sectionPad}>
      <div style={sectionInner}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(200px, 280px) minmax(0, 1fr)',
            gap: 32,
            alignItems: 'flex-start',
          }}
        >
          <div
            aria-hidden
            style={{
              aspectRatio: '2 / 3',
              borderRadius: 'var(--radius-lg, 16px)',
              background: s.posterUrl
                ? `url(${s.posterUrl}) center/cover no-repeat`
                : 'var(--color-surface-muted, #e4e4e7)',
              boxShadow: 'var(--shadow-md, 0 10px 30px -12px rgba(0,0,0,0.35))',
            }}
          />
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 'clamp(24px, 4vw, 32px)',
                fontWeight: 700,
                color: 'var(--color-text-primary, #18181b)',
              }}
            >
              {s.title}
            </h1>
            {s.director && (
              <div style={{ marginTop: 4, color: 'var(--color-text-secondary, #71717a)' }}>
                {t('mediaDetail.director', { name: s.director })}
              </div>
            )}
            {s.description && <p style={{ marginTop: 12, lineHeight: 1.6 }}>{s.description}</p>}
            <div
              style={{
                marginTop: 12,
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-tertiary, #94867d)',
                fontFamily: 'var(--font-family-mono, monospace)',
              }}
            >
              {s.releaseYear && <>{s.releaseYear} · </>}
              {t('seriesDetail.episodeCount', { count: eps.length })}
              {s.episodeRuntime && <> · {s.episodeRuntime}</>}
            </div>
          </div>
        </div>
        <section style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 'var(--font-size-lg, 18px)', fontWeight: 600, marginBottom: 12 }}>
            {t('seriesDetail.episodesHeading')}
          </h2>
          {episodeListStyle === 'grid' || episodeListStyle === 'rail' ? (
            <div
              {...part('list')}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))',
                gap: 16,
              }}
            >
              {eps.map((e) => (
                <EpisodeCard
                  key={e.id}
                  episode={e}
                  style="rail"
                  showEpisodeNumber={showEpisodeNumbers}
                  seriesSlug={s.slug}
                  {...(episodeHrefTemplate ? { hrefTemplate: episodeHrefTemplate } : {})}
                />
              ))}
            </div>
          ) : (
            <div {...part('list')}>
              {eps.map((e) => (
                <EpisodeCard
                  key={e.id}
                  episode={e}
                  style={episodeListStyle}
                  showEpisodeNumber={showEpisodeNumbers}
                  seriesSlug={s.slug}
                  {...(episodeHrefTemplate ? { hrefTemplate: episodeHrefTemplate } : {})}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </BlockRoot>
  );
}
